import { UserData } from '@vitesse/proto/vitesse/v1/user_data_pb.js';
import '@vitesse/base-ui/elements/vitesse-editable-description';
import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
import { getSession } from '@vitesse/base-ui';
import { ProgramIndex } from '@vitesse/proto/vitesse/v1/program_index_pb.js';
import { Controllers } from '@vitesse/proto/vitesse/v1/controller_pb.js';
import { Plugins } from '@vitesse/proto/vitesse/v1/command_pb.js';
import '@vitesse/base-ui/elements/vitesse-gate-overlay';
import { discoveryBadge, statusText, type PillTab } from '@vitesse/base-ui/components/templates';
import { t, formatNumber } from '@vitesse/base-ui/i18n';
import type { BackendClient } from '../bridge/backend-client';
import { button } from '@vitesse/base-ui/components/base-buttons';
import type { PluginTypesManager } from '../managers/plugin-types-manager';
import { showResultError } from '../errors';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';
import './vitesse-add-symbol-dialog';
import type { Controller, PropertiesView } from '../../core/domain/controller';
import type { StreamFrame } from '../../core/bridge/types';
import type { DescriptionRow } from '../../workers/backend.worker';
import { SymbolGrid } from './symbolGrid';
import { StubSymbolDataSource } from './symbolGrid/stubDataSource';
import { IndexerPagedView } from './symbolGrid/indexerPagedView';
import { controllerPagedBackend } from './symbolGrid/pagedBackend';
import type { SymbolGridDataSource } from './symbolGrid/dataSource';
import './vitesse-code-browser';
import '../components/controller/controller-logs-section';
import '../components/controller/controller-history-section';
import '../components/properties-view';
import { AiAdmin } from '@vitesse/proto/vitesse/ai/v1/ai_admin_pb.js';
import type { ControllerOpen, ControllerTab as RouteControllerTab } from './router';
import '@vitesse/base-ui/elements/vitesse-gated';
import '../components/findings/findings-list';
import type { FindingsManager } from '../managers/findings-manager';
import type { FindingNames } from '../components/findings/finding-labels';
import type { CodeBlock } from './vitesse-code-browser';
import type { ProgramBlock } from '../../core/bridge/connect-client';

/**
 * The detail view drives one of two source shapes:
 *  - IndexerPagedView (real backend): IS the grid's view too; its lifecycle is
 *    activate()/destroy(), driven by the grid's mount()/destroy().
 *  - StubSymbolDataSource (no backend): a client-tree source with start()/stop().
 * The optional members below cover both without a discriminated union.
 * (Everything is always subscribed — there is no subscribe toggle.)
 */
type GridSource = SymbolGridDataSource & {
  start?(): void | Promise<void>;
  stop?(): void;
};

/** The controller detail sub-views (named in the router, which opens them). Symbols is the default + primary. */
type ControllerTab = RouteControllerTab;

/*
 * <vitesse-controller-detail> — the PLC detail view, symbols tab only.
 *
 * LIGHT DOM (createRenderRoot → this) so the agent's real SCSS — `.base-page-*`
 * for the header and `.vg-*` for the grid — styles the markup identically (the
 * agent's main.scss is imported globally in main/index.ts). This is the same
 * light-DOM + agent-markup + agent-CSS pattern as vitesse-tree.ts.
 *
 * It mounts the ported vanilla-TS SymbolGrid (80-row pooled virtual renderer,
 * Canvas sparklines, search, expand/collapse — see ./symbolGrid). The grid is
 * decoupled from any data layer behind `SymbolGridDataSource`; this view feeds
 * it a StubSymbolDataSource so the whole thing renders + ticks with no backend.
 *
 * Dropped vs the agent's ControllerDetailPage (per port scope): the Properties
 * and Program tabs, connect/disconnect/discovery action buttons, and the
 * pin/edit/delete/add-symbol dialogs.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * TODO(real-backend): replace `StubSymbolDataSource` with a
 * `WorkerSymbolDataSource` that folds this.backend's subscribeSymbols /
 * subscribeValues MessagePort streams into the SymbolGridDataSource shape —
 * see the big TODO block in ./symbolGrid/stubDataSource.ts. The `backend`
 * property is already threaded here for exactly that hookup. Nothing in
 * SymbolGrid / SymbolTreeView changes; only the source swaps.
 * ──────────────────────────────────────────────────────────────────────────
 */
@customElement('vitesse-controller-detail')
export class ControllerDetail extends BasePage {
  // Threaded through for the real-backend hookup (see TODO above). Unused by the
  // stub path today, but kept so the wiring point is obvious.
  @property({ attribute: false }) backend?: BackendClient;
  @property() controllerId = '';
  /** Parent gateway id (used to scope backend lookups). */
  @property() gatewayId = '';
  /** Parent gateway display name — the back-button label (where “back” lands). */
  @property() gatewayName = '';
  /** Optional full descriptor (for header status badges); falls back to id-only. */
  @property({ attribute: false }) controller?: Controller;
  /** The gateway's plugin types — resolves this controller's add/delete capabilities. */
  @property({ attribute: false }) pluginTypesManager?: PluginTypesManager;
  /** The org's findings log, and what its ids are called — for the Findings tab. */
  @property({ attribute: false }) findings?: FindingsManager;
  @property({ attribute: false }) findingNames?: FindingNames;
  /**
   * What this page was asked to open with — a link, or the assistant moving the person (router
   * `parseControllerOpen`). Stamped by the shell, so the same ask made twice is still a change.
   */
  @property({ attribute: false }) open?: ControllerOpen & { seq: number };

  @state() private tab: ControllerTab = 'symbols';
  /**
   * The arrival handed on to the History tab, until the person moves tabs themselves. Kept apart
   * from `open` because the History section is re-created each time its tab is shown: handed `open`
   * directly, coming back to History later would re-apply the arrival over the lanes the person
   * has picked since.
   */
  @state() private historyOpen?: ControllerOpen & { seq: number };
  @state() private addSymbolOpen = false;
  @state() private deleteOpen = false;
  @state() private deleting = false;
  @state() private deleteError = '';

  private grid: SymbolGrid | null = null;
  private source: GridSource | null = null;

  // Program tab (lazy — fetched only when the tab is first opened, so the
  // on-demand cloud OBJ mirror only spins up on demand).
  @state() private programBlocks: CodeBlock[] = []; // the document body order
  @state() private programState: 'idle' | 'loading' | 'ready' | 'empty' | 'error' = 'idle';

  // Per-controller description (user_data), fetched + streamed from the backend.
  // descriptions holds the gateway's whole (tiny) set; we render the current cid's.
  private descPort?: MessagePort;
  private descSubscribed = false;
  private descriptions = new Map<string, string>();
  // pluginTypesManager is a shared EventTarget whose data resolves AFTER mount;
  // subscribe to its 'change' so the Add/Delete actions (gated on the plugin's
  // advertised capabilities) appear when it resolves, instead of waiting for some
  // unrelated re-render to happen to pick the resolved type up.
  private readonly ptChangeHandler = (): void => this.requestUpdate();
  private ptSubscribed?: PluginTypesManager;

  connectedCallback(): void {
    super.connectedCallback();
    void this.openDescriptions(); // fetch + stream descriptions on enter
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardownGrid();
    this.closeDescriptions(); // unsubscribe on leave
    this.ptSubscribed?.removeEventListener('change', this.ptChangeHandler);
    this.ptSubscribed = undefined;
  }

  // Grid lifecycle across tabs (after render, so the host div exists):
  //  - controllerId change → tear down (re-mount fresh for the new PLC).
  //  - mount ONLY while the Symbols tab is visible: the grid sizes itself from
  //    its host, and mounting it while `?hidden` (display:none) would mis-measure
  //    (it has no ResizeObserver). Switching AWAY from Symbols keeps the grid
  //    mounted-but-hidden so scroll + expansion state survive a tab round-trip.
  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('open')) {
      if (this.open) this.tab = this.open.tab;
      this.historyOpen = this.open;
    }
  }

  protected updated(changed: PropertyValues): void {
    if (changed.has('pluginTypesManager') && this.pluginTypesManager !== this.ptSubscribed) {
      this.ptSubscribed?.removeEventListener('change', this.ptChangeHandler);
      this.ptSubscribed = this.pluginTypesManager;
      this.ptSubscribed?.addEventListener('change', this.ptChangeHandler);
      this.requestUpdate(); // the type may have resolved before we subscribed
    }
    if (changed.has('controllerId') || changed.has('gatewayId')) {
      // Tear down on EITHER axis: two gateways can host a controller with the SAME
      // id (e.g. each gateway's "Core"), so an A/Core → B/Core hop changes only
      // gatewayId — the grid (its IndexerPagedView bakes in the OLD gatewayId) must
      // be rebuilt, else it keeps painting gateway A's symbols. Same for the program.
      this.teardownGrid();
      this.programState = 'idle'; // new PLC → re-fetch its program when next opened
      this.programBlocks = [];
    }
    /*
     * A PROGRAM CHANGES WHILE THE PLC IS AWAY, AND THAT IS THE NORMAL CASE.
     *
     * Somebody edits it in the vendor IDE: the PLC drops, the plugin sits in reconnect backoff, and
     * when it comes back it re-extracts the blocks whose CompileTime (or st_hash) moved. All of
     * that is right, and the page did not notice any of it — the skeleton was fetched ONCE, lazily,
     * the first time the Program tab was opened for this controller, and never again. So the tab
     * kept showing the program from before the edit until the page was reloaded, which is the worst
     * kind of stale: source that looks authoritative and is describing a machine that has moved on.
     *
     * Two moments re-read it, and they are the two that mean something:
     *
     *   · the controller comes BACK — any status → connected. That is the edit landing.
     *   · discovery finishes — any state → running. Extraction is deliberately deferred behind the
     *     first poll cycle (the value pipeline gets the PLC's session table first), so the program
     *     is frequently NOT yet republished at the instant the connection flips. This is the second
     *     look that catches it.
     *
     * A snapshot RPC either way, and it only repaints when the program HASH differs — so a
     * reconnect that changed nothing costs one cheap read and no flicker.
     */
    if (changed.has('controller') && this.programState === 'ready') {
      const before = changed.get('controller') as { status?: string; discoveryStatus?: string } | undefined;
      const now = this.controller;
      const cameBack = before?.status !== 'connected' && now?.status === 'connected';
      const discovered = before?.discoveryStatus !== 'running' && now?.discoveryStatus === 'running';
      if (before && (cameBack || discovered)) void this.refreshProgram();
    }
    if (this.tab === 'symbols' && this.controllerId) {
      if (!this.grid) this.mountGrid();
      else if (changed.has('controller')) {
        // The grid is a dumb, backend-pushed viewport: its structure stream
        // auto-updates rows + scrollbar size as the catalog grows or changes
        // (discovery landing more, add, delete) — the page NEVER compares counts to
        // decide a refresh. We only forward the live connection status (the
        // descriptor stream carries it), which drives the grid's connected chrome.
        this.grid.updateConnectionStatus(this.connected);
      }
    }
    // Lazy program fetch — only the first time the Program tab is shown for this PLC,
    // and only when the plan permits it. The overlay paints a skeleton for a denied
    // reader; firing the fetch anyway would be the doomed request it exists to avoid.
    if (
      this.tab === 'program' &&
      this.programState === 'idle' &&
      getSession()?.allowsRpc(ProgramIndex.method.getProgram) !== false
    )
      void this.loadProgram();
    if (changed.has('backend')) void this.openDescriptions(); // backend arrived late → start the stream
  }

  private get connected(): boolean {
    // No descriptor → assume reachable (stub is always "connected").
    return this.controller ? this.controller.status === 'connected' : true;
  }

  private mountGrid(): void {
    const host = this.querySelector<HTMLDivElement>('.vg-host') ?? undefined;
    if (!host || !this.controllerId) return;

    // Real backend → IndexerPagedView: server-linearized, viewport-windowed,
    // stateless (the browser holds only a ~600-row window). It IS the grid's view,
    // so the grid's mount() activates it — no start() here. No-backend fallback →
    // the synthetic StubSymbolDataSource (client tree, start()/stop()).
    // plugin_id = the multiplexed container that owns this controller. One
    // container per protocol holds N controllers, named `<driver>-plugin`
    // (e.g. "mock" → "mock-plugin"). It routes the setPollingPriority cmd.
    const pluginId = `${this.controller?.protocol || 'mock'}-plugin`;
    /*
     * Whether THIS plugin enforces the write-lock, from its own descriptor.
     *
     * Undefined while the descriptor is still resolving, and that is deliberately
     * read as FALSE rather than "assume yes": the padlock is the estop protection,
     * and offering it before we know the plugin honours it shows something that
     * looks like safety and is not. It appears a moment later once the descriptor
     * lands, which is the right direction to be wrong in.
     */
    const canLock = this.pluginTypesManager?.byId(pluginId)?.canLockSymbols === true;
    this.source = this.backend
      ? new IndexerPagedView(
          controllerPagedBackend(this.gatewayId, this.controllerId, this.backend, pluginId, canLock),
        )
      : new StubSymbolDataSource(this.controllerId);
    this.grid = new SymbolGrid({ container: host, dataSource: this.source });
    this.grid.mount(this.connected); // activates the view (IndexerPagedView or SymbolTreeView)
    void this.source.start?.(); // stub only; the paged view is activated by mount()
  }

  private teardownGrid(): void {
    this.grid?.destroy(); // also destroys the view (closes the paged view's value port)
    this.grid = null;
    this.source?.stop?.(); // stub only; the paged view tears down via grid.destroy()
    this.source = null;
  }

  // ── controller description (user_data) ─────────────────────────────────────
  private async openDescriptions(): Promise<void> {
    if (this.descSubscribed || !this.backend) return; // already open, or no backend (stub mode)
    this.descSubscribed = true; // claim synchronously so concurrent calls can't double-open
    const port = await this.backend.api.subscribeControllerDescriptions(this.gatewayId);
    if (!this.isConnected) {
      port.close(); // disconnected during the await
      this.descSubscribed = false;
      return;
    }
    this.descPort = port;
    port.onmessage = (e: MessageEvent<StreamFrame<DescriptionRow>>) => {
      const frame = e.data;
      if (frame.kind !== 'snapshot') return;
      this.descriptions = new Map(frame.items.map((d) => [d.controllerId, d.description]));
      this.requestUpdate();
    };
  }
  private closeDescriptions(): void {
    this.descPort?.postMessage({ kind: 'close' });
    this.descPort?.close();
    this.descPort = undefined;
    this.descSubscribed = false;
  }

  private get description(): string {
    return this.descriptions.get(this.controllerId) ?? '';
  }

  private commitDescription(next: string): void {
    if (next !== this.description) {
      void this.backend?.api
        .putControllerDescription(this.gatewayId, this.controllerId, next)
        .catch((err: unknown) => showResultError(err, t('controller.descriptionWriteFailed')));
    }
  }

  protected renderDescription() {
    // Editing is backend-only (the stub has no putControllerDescription); fall back
    // to a plain line when there's no backend. The description is readable by anyone
    // who can see the controller; EDITING it is org:controller:annotate — a member
    // default, so this changes nothing for an ordinary member and everything for one
    // an admin revoked it from, which is the entire reason the action was given a name.
    const canEdit = !!this.backend && getSession()?.allowsRpc(UserData.method.putControllerDescription) !== false;
    return html`<vitesse-editable-description
      .value=${this.description}
      placeholder=${t('controller.descriptionFallback')}
      hint=${t('controller.descriptionEditHint')}
      .canEdit=${canEdit}
      .commit=${(next: string) => this.commitDescription(next)}
    ></vitesse-editable-description>`;
  }

  protected onBack(): void {
    this.dispatchEvent(
      new CustomEvent('navigate', { detail: { view: 'gateway' }, bubbles: true, composed: true }),
    );
  }

  private statusBadge() {
    const c = this.controller;
    if (!c) return nothing;
    const variant =
      c.status === 'connected'
        ? 'base-status-badge-green'
        : c.status === 'connecting' || c.status === 'reconnecting'
          ? 'base-status-badge-yellow'
          : 'base-status-badge-gray';
    const label = t('controller.statusBadge', { status: statusText(c.status) });
    return html`<span class="base-status-badge ${variant}"><span class="base-status-badge-label">${label}</span></span>`;
  }

  private discoveryBadge() {
    const d = this.controller?.discoveryStatus;
    if (!d) return nothing;
    return discoveryBadge(d);
  }

  private symbolsBadge() {
    const n = this.controller?.symbolCount ?? 0;
    if (n <= 0) return nothing;
    return html`<span class="base-status-badge base-status-badge-blue"
      ><span class="base-status-badge-label">${t('controller.symbolCount', { n: formatNumber(n) })}</span></span
    >`;
  }

  // ── BasePage hooks ─────────────────────────────────────────────────────────
  protected pageTitle(): string {
    return this.controller?.name || this.controllerId;
  }
  protected backLabel(): string {
    return this.gatewayName || t('controller.back');
  }
  protected backAriaLabel(): string {
    return t('controller.ariaBack');
  }
  // AI hints follow the active tab — the panel suggests questions about what the
  // user is actually looking at (program / logs / properties / symbols).
  protected aiHints(): string[] {
    switch (this.tab) {
      case 'program':
        return [t('controller.hintExplain'), t('controller.hintProgramWalk')];
      case 'logs':
        return [t('controller.hintLogsRecent'), t('controller.hintLogsError')];
      case 'findings':
        return [t('findings.hintController'), t('findings.hintOpen')];
      case 'properties':
        return [t('controller.hintPropsConfig'), t('controller.hintPropsCheck')];
      case 'symbols':
      default:
        return [t('controller.hintPriority'), t('controller.hintSymbolsLive')];
    }
  }
  protected renderBadges() {
    return html`${this.statusBadge()}${this.discoveryBadge()}${this.symbolsBadge()}`;
  }
  // Backend-driven actions: add-symbol + delete show only when THIS controller's
  // plugin advertises the capability (resolved via its plugin_id). Add-symbol is
  // further restricted to the Symbols tab — it's an action ON the symbol catalog,
  // so it has no business floating over Properties/Program/Logs/History.
  protected renderActions() {
    const c = this.controller;
    const pt = c ? this.pluginTypesManager?.byId(c.pluginId) : undefined;
    if (!c || !pt) return nothing;
    return html`${pt.canAddSymbols && this.tab === 'symbols'
      ? html`<vitesse-gated .rpc=${Plugins.method.addSymbols} inline>
          ${button({ label: 'Add symbol', icon: 'plus', color: 'blue', onClick: () => (this.addSymbolOpen = true) })}
        </vitesse-gated>`
      : nothing}${pt.canManuallyDelete
      ? html`<vitesse-gated .rpc=${Plugins.method.removeController} inline>
          ${button({
            label: 'Delete',
            icon: 'trash-2',
            color: 'red',
            onClick: () => {
              this.deleteError = '';
              this.deleteOpen = true;
            },
          })}
        </vitesse-gated>`
      : nothing}`;
  }
  private async confirmDelete(): Promise<void> {
    const c = this.controller;
    if (!c || !this.backend || this.deleting) return;
    this.deleting = true;
    this.deleteError = '';
    try {
      const res = await this.backend.api.removeController(this.gatewayId, c.pluginId, c.id);
      if (res.ok) {
        this.deleteOpen = false;
        this.onBack(); // the controller's gone — return to the gateway
      } else {
        this.deleteError = res.error || 'Could not delete the controller.';
      }
    } catch (err) {
      this.deleteError = err instanceof Error ? err.message : String(err);
    } finally {
      this.deleting = false;
    }
  }

  // ── tabs (Symbols / Properties / Program) — BasePage renders the bar ─────────
  protected tabs(): PillTab[] {
    const n = this.controller?.symbolCount ?? 0;
    return [
      { id: 'symbols', label: t('controller.tabSymbols'), icon: 'list-tree', count: n > 0 ? n : undefined },
      { id: 'history', label: t('controller.tabHistory'), icon: 'history' },
      { id: 'properties', label: t('controller.tabProperties'), icon: 'settings' },
      { id: 'program', label: t('controller.tabProgram'), icon: 'code-xml' },
      { id: 'logs', label: t('controller.tabLogs'), icon: 'scroll-text' },
      { id: 'findings', label: t('findings.title'), icon: 'lightbulb' },
    ];
  }
  protected activeTabId(): string {
    return this.tab;
  }
  protected onTabChange(id: string): void {
    if (id === this.tab) return;
    this.historyOpen = undefined;
    this.tab = id as ControllerTab; // re-render hides/shows panels; updated() mounts the grid on return to Symbols
  }

  /** The entity path of this PLC — threaded into a request-access ask so the
   *  admin sees WHERE the wall was hit. The one path scheme used everywhere. */
  private entityPath(): string {
    return `gateways/${this.gatewayId}/controllers/${this.controllerId}`;
  }

  protected renderContent() {
    // Symbols host is ALWAYS in the DOM (CSS-hidden off-tab) so the grid persists
    // its scroll + expansion across tab switches. Properties + Program render
    // only while active — Program lazily, so its program-view (and the on-demand
    // cloud mirror it triggers) only spins up when the user actually opens it.
    const pt = this.controller ? this.pluginTypesManager?.byId(this.controller.pluginId) : undefined;
    return html`
      <div class="vg-host" ?hidden=${this.tab !== 'symbols'}></div>
      ${this.tab === 'history' ? this.renderHistory() : nothing}
      ${this.tab === 'properties'
        ? html`<vitesse-gate-overlay
            capability="telemetry"
            level="flows"
            context=${this.entityPath()}
            .content=${this.renderProperties()}
          ></vitesse-gate-overlay>`
        : nothing}
      ${this.tab === 'program'
        ? html`<vitesse-gate-overlay
            .rpc=${ProgramIndex.method.getProgram}
            context=${this.entityPath()}
            .content=${this.renderProgram()}
          ></vitesse-gate-overlay>`
        : nothing}
      ${this.tab === 'findings' ? this.renderFindings() : nothing}
      ${this.tab === 'logs'
        ? html`<vitesse-gate-overlay
            .rpc=${Controllers.method.subscribeLogs}
            context=${this.entityPath()}
            .content=${this.renderLogs()}
          ></vitesse-gate-overlay>`
        : nothing}
      <vitesse-confirm-dialog
        .open=${this.deleteOpen}
        heading="Delete controller"
        message=${`Delete "${this.controller?.name ?? ''}"?`}
        detail="Its address is blacklisted from re-discovery until you add it again."
        confirm-label="Delete"
        danger
        ?busy=${this.deleting}
        error=${this.deleteError}
        @confirm=${() => void this.confirmDelete()}
        @dialog-close=${() => (this.deleteOpen = false)}
      ></vitesse-confirm-dialog>
      ${this.controller
        ? html`<vitesse-add-symbol-dialog
            .open=${this.addSymbolOpen}
            .gatewayId=${this.gatewayId}
            .controllerId=${this.controllerId}
            .pluginId=${this.controller.pluginId}
            .fields=${pt?.addSymbolForm ?? []}
            .backend=${this.backend}
            @dialog-close=${() => (this.addSymbolOpen = false)}
            @added=${() => (this.addSymbolOpen = false)}
          ></vitesse-add-symbol-dialog>`
        : nothing}
    `;
  }

  // ── Findings tab — the log's entries about THIS machine ────────────────────
  // Scoped by relay AND controller: every relay has a "Core", and an entry about another relay's
  // is not about this one. Opens on All — on a machine's page the question is what has been found
  // about it, not only what is waiting.
  private renderFindings() {
    // `.content`, as Program and Logs are, so a reader without ai:finding:view never mounts the list
    // that would load itself behind the lock.
    return html`<vitesse-gate-overlay
      .rpc=${AiAdmin.method.listFindings}
      context=${this.entityPath()}
      .content=${html`<vitesse-findings-list
        .manager=${this.findings}
        .names=${this.findingNames}
        .scope=${{ controllerId: `${this.gatewayId}/${this.controllerId}` }}
        defaultTab="all"
      ></vitesse-findings-list>`}
    ></vitesse-gate-overlay>`;
  }

  // ── Logs tab — the controller's live, plugin-emitted log tail ───────────────
  // The <vitesse-controller-logs> element owns the stream lifecycle; it's only
  // in the DOM while this tab is active, so the api-gateway NATS subscription
  // (and any cloud cost) exists only while someone is actually watching.
  private renderLogs() {
    return html`<vitesse-controller-logs
      .backend=${this.backend}
      .gatewayId=${this.gatewayId}
      .controllerId=${this.controllerId}
    ></vitesse-controller-logs>`;
  }

  // ── History tab — bare-bones vertical slice (one hardcoded symbol, QueryRange
  // only, fixed range); no backend → keep the stub-mode placeholder. ──────────
  private renderHistory() {
    if (!this.backend) {
      return html`<div class="controller-program-empty">${t('controller.historyEmpty')}</div>`;
    }
    return html`<vitesse-controller-history
      .backend=${this.backend}
      .gatewayId=${this.gatewayId}
      .controllerId=${this.controllerId}
      .open=${this.historyOpen}
    ></vitesse-controller-history>`;
  }

  // ── Properties tab — the plugin-authored Properties view ─────────────────────
  // The plugin decides what to show (titled tables); <vitesse-properties-view>
  // renders them in the shared card chrome. For a plugin that hasn't reported any
  // properties yet, we synthesize a basic Overview sheet from the descriptor so
  // the tab is never empty — rendered through the SAME component for one style.
  private renderProperties() {
    const c = this.controller;
    const view: PropertiesView = c?.properties?.tables.length
      ? c.properties
      : this.fallbackProperties(c);
    return html`<vitesse-properties-view .view=${view}></vitesse-properties-view>`;
  }

  private fallbackProperties(c?: Controller): PropertiesView {
    if (!c) return { tables: [] };
    const rows: string[][] = [
      ['Driver', c.protocol || '—'],
      ['Address', c.address || '—'],
      ['Model', c.model || '—'],
      ['Firmware', c.firmware || '—'],
      ['Status', statusText(c.status)],
      ['Discovery', c.discoveryStatus || '—'],
      ['Symbols', formatNumber(c.symbolCount)],
    ];
    if (c.status === 'error' && c.errorDetail) rows.push(['Error', c.errorDetail]);
    return { tables: [{ title: 'Overview', columns: [], rows }] };
  }

  // ── Program tab — the reusable IDE code-browser over the PLC program ──────────
  // Preload-then-reconcile: paint the cached skeleton (IndexedDB, cross-session)
  // instantly, then fetch live and rebuild only if the programHash moved. The
  // gw/cid capture guards against a controller switch landing on the wrong PLC.
  private async loadProgram(): Promise<void> {
    if (!this.backend || !this.controllerId) {
      this.programState = 'empty';
      return;
    }
    // Don't fetch a program the org cannot read. <vitesse-code-browser> already
    // renders the lock, so the user sees the right thing either way — but without
    // this the tab fires RPCs the server is certain to refuse, every time it is
    // opened. Asking first is the same answer for free.
    //
    // Both verbs, though they carry one capability today: this load reaches the
    // program two ways — the block tree, then each block's source — and naming only
    // one would leave the other unguarded the day they diverge.
    const session = getSession();
    const mayReadProgram =
      session?.allowsRpc(ProgramIndex.method.blockTree) !== false &&
      session?.allowsRpc(ProgramIndex.method.getBlockSource) !== false;
    if (!mayReadProgram) {
      this.programState = 'empty';
      return;
    }
    const gw = this.gatewayId;
    const cid = this.controllerId;
    this.programState = 'loading';
    this.programHash = '';
    try {
      const cached = await this.backend.api.getCachedProgramSkeleton(gw, cid);
      if (this.gatewayId !== gw || this.controllerId !== cid) return; // switched mid-load
      if (cached && cached.blocks.length > 0) {
        this.programHash = cached.programHash;
        this.applyProgram(cached.blocks); // instant paint; reconciled below
      }
    } catch {
      /* cache miss / read failure → fall through to the live fetch */
    }
    try {
      const fresh = await this.backend.api.fetchProgramSkeleton(gw, cid);
      if (this.gatewayId !== gw || this.controllerId !== cid) return;
      // Rebuild only when the live program differs from what we painted.
      if (fresh.programHash !== this.programHash || this.programState === 'loading') {
        this.programHash = fresh.programHash;
        this.applyProgram(fresh.blocks);
      }
    } catch (e) {
      /*
       * Said out loud, not only rendered. The visible state collapses every cause to one sentence
       * ("Could not load this block's source"), and the cause matters: this exact catch swallowed a
       * deadline_exceeded for a day while the fault was hunted through the indexer, the broker and
       * two rebuilt containers. Keep a cached paint if we have one; only surface an error state
       * with nothing shown.
       */
      console.error('[program] skeleton fetch failed', e);
      if (this.programState === 'loading') this.programState = 'error';
    }
  }

  /** The hash of the program currently painted, so a re-read can tell "unchanged" from "moved on"
   *  without diffing the blocks. */
  private programHash = '';

  /**
   * Re-read the skeleton WITHOUT blanking what is on screen.
   *
   * Deliberately not `loadProgram()`: that one paints "Loading…" first, which is right for the
   * first read and wrong for a refresh — the program is already there, and replacing it with a
   * spinner for a fetch that will usually return the same hash is a flicker in exchange for
   * nothing. Silent on failure for the same reason: the last good program keeps its place, and the
   * reason is in the console for whoever is looking.
   */
  private async refreshProgram(): Promise<void> {
    const backend = this.backend;
    if (!backend || !this.controllerId) return;
    const gw = this.gatewayId;
    const cid = this.controllerId;
    try {
      const fresh = await backend.api.fetchProgramSkeleton(gw, cid);
      if (this.gatewayId !== gw || this.controllerId !== cid) return; // switched mid-fetch
      if (fresh.programHash === this.programHash) return; // the edit changed nothing we show
      this.programHash = fresh.programHash;
      this.applyProgram(fresh.blocks);
    } catch (e) {
      console.error('[program] refresh after reconnect failed', e);
    }
  }

  /** Build the document body from a block list and show it. */
  private applyProgram(blocks: ProgramBlock[]): void {
    const body = buildProgramBlocks(blocks);
    this.programBlocks = body;
    this.programState = body.length > 0 ? 'ready' : 'empty';
  }

  // Stable identity (captures `this`; reads the live ids at call time) so the
  // browser's loadSource prop doesn't churn each render.
  private readonly loadBlockText = async (key: string): Promise<string> => {
    const src = await this.backend!.api.fetchBlockSource(this.gatewayId, this.controllerId, key);
    return src.text;
  };

  private renderProgram() {
    if (this.programState === 'loading') {
      return html`<div class="controller-program-empty">${t('controller.programLoading')}</div>`;
    }
    if (this.programState === 'empty' || this.programState === 'idle') {
      return html`<div class="controller-program-empty">${t('controller.programEmpty')}</div>`;
    }
    if (this.programState === 'error') {
      return html`<div class="controller-program-empty">${t('controller.programSourceError')}</div>`;
    }
    return html`<vitesse-code-browser
      .blocks=${this.programBlocks}
      .loadSource=${this.loadBlockText}
      searchPlaceholder=${t('grid.searchPlaceholder')}
      emptyHint=${t('controller.programEmpty')}
    ></vitesse-code-browser>`;
  }
}

// ── program skeleton → document body ───────────────────────────────────────────
// Vendor-agnostic: blocks are grouped by family (the four named ones first, then
// the rest A–Z) ONLY to give the continuous document a stable, readable order.
function buildProgramBlocks(blocks: ProgramBlock[]): CodeBlock[] {
  const groups = new Map<string, ProgramBlock[]>();
  for (const b of blocks) {
    if (b.childCount > 0) continue; // skip grouping/placeholder nodes — leaves only
    const type = b.blockType || 'BLOCK';
    const arr = groups.get(type);
    if (arr) arr.push(b);
    else groups.set(type, [b]);
  }
  const order = ['OB', 'FB', 'FC', 'DB', 'UDT', 'SFB', 'SFC'];
  const types = [...groups.keys()].sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });

  const body: CodeBlock[] = [];
  for (const type of types) {
    const sorted = (groups.get(type) ?? [])
      .slice()
      .sort((a, b) => (a.name || `${type}${a.blockNumber}`).localeCompare(b.name || `${type}${b.blockNumber}`, undefined, { numeric: true }));
    for (const b of sorted) {
      body.push({ key: b.blockId, label: b.name || `${type}${b.blockNumber}`, language: 'st', type, estBytes: b.stBytes });
    }
  }
  return body;
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-controller-detail': ControllerDetail;
  }
}
